import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { authApi } from '@app/store/slices/auth/authApi';
import { tasksApi } from '@app/store/slices/tasks/tasksApi';
import { balanceApi } from '@app/store/slices/balance/balanceApi';
import { withdrawalApi } from '@app/store/slices/withdrawal/withdrawalApi';
import { usersApi } from '@app/store/slices/users/usersApi';
import { contentApi } from '@app/store/slices/content/contentApi';
import { stonesApi } from '@app/store/slices/stones/stonesApi';
import { friendsApi } from '@app/store/slices/friends/friendsApi';
import { settlementsApi } from '@app/store/slices/settlements/settlementsApi';
import { dailyRewardApi } from '@app/store/slices/dailyReward/dailyRewardApi';
import { userProgressApi } from '@app/store/slices/userProgress/userProgressApi';
import { adminApi } from '@app/store/slices/admin/adminApi';
import { tonPriceApi } from '@app/store/slices/tonPrice/tonPriceApi';
import { paymentApi } from '@app/store/slices/payment/paymentApi';
import { matrixApi } from '@app/store/slices/matrix/matrixApi';
import authSlice from '@app/store/slices/auth/authSlice';
import { tutorialReducer } from '@app/store/slices/tutorial/tutorialSlice';
import { commonReducer } from '@app/store/slices/common/commonSlice';
import { modalReducer } from './slices/modal/modalSlice';
import { errorLoggerMiddleware } from './utils/error-logger-middleware';
import { reduxActionCaptureMiddleware } from './utils/redux-logger-middleware';

export const store = configureStore({
  reducer: {
    auth: authSlice,
    [authApi.reducerPath]: authApi.reducer,
    [tasksApi.reducerPath]: tasksApi.reducer,
    [balanceApi.reducerPath]: balanceApi.reducer,
    [withdrawalApi.reducerPath]: withdrawalApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [contentApi.reducerPath]: contentApi.reducer,
    [stonesApi.reducerPath]: stonesApi.reducer,
    [friendsApi.reducerPath]: friendsApi.reducer,
    [settlementsApi.reducerPath]: settlementsApi.reducer,
    [dailyRewardApi.reducerPath]: dailyRewardApi.reducer,
    [userProgressApi.reducerPath]: userProgressApi.reducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [tonPriceApi.reducerPath]: tonPriceApi.reducer,
    [paymentApi.reducerPath]: paymentApi.reducer,
    [matrixApi.reducerPath]: matrixApi.reducer,
    tutorial: tutorialReducer,
    common: commonReducer,
    modal: modalReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      authApi.middleware,
      tasksApi.middleware,
      balanceApi.middleware,
      withdrawalApi.middleware,
      usersApi.middleware,
      contentApi.middleware,
      stonesApi.middleware,
      friendsApi.middleware,
      settlementsApi.middleware,
      dailyRewardApi.middleware,
      userProgressApi.middleware,
      adminApi.middleware,
      tonPriceApi.middleware,
      paymentApi.middleware,
      matrixApi.middleware,
      reduxActionCaptureMiddleware,
      errorLoggerMiddleware
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
